import * as React from "react";
import {
  GridColDef,
  GridRowsProp,
  GridSortModel,
  GridRowClassNameParams,
  GridRenderCellParams,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import {
  Box,
  Chip,
  Link,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import NoResultsOverlay from "../../components/NoResultOverlay";
import { useTitle } from "../../hooks/useTitle";
import { useGetWithdrawalsQuery } from "../../services/withdrawal.service";
import { Withdrawal } from "../../types/withdrawal";
import dayjs from "dayjs";
import { formatMoney } from "../../utils/helper";
import TalentDrawer from "../talent/TalentDrawer";
import { CardBase, GlassDataGrid, StyledTextField, Button, OrangeLinearProgress } from "../../components/global";
import { Download as DownloadIcon } from "@mui/icons-material";

const WithdrawalsPage = () => {
  useTitle("Withdrawals");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(25);
  const [sortModel, setSortModel] = React.useState<GridSortModel>([]);
  const [searchQuery, setSearchQuery] = React.useState<string | undefined>(
    undefined
  );

  const [openTalent, setOpenTalent] = React.useState(false);
  const [currentTalent, setCurrentTalent] = React.useState(null);

  const onViewTalent = (item: any) => {
    setCurrentTalent(item);
    setOpenTalent(true);
  };

  const { data, isLoading, isFetching } = useGetWithdrawalsQuery({
    page,
    pageSize,
    sortField: sortModel.length > 0 ? sortModel[0].field : undefined,
    sortMode: sortModel.length > 0 ? sortModel[0].sort : undefined,
    search: searchQuery,
  });
  const rowCount = React.useMemo(() => data?.meta.total, [data]);

  const rows: GridRowsProp | undefined = React.useMemo(
    () => data?.data,
    [data]
  );

  const handleExport = React.useCallback(() => {
    if (!rows || rows.length === 0) return;

    const headers = ["Time", "Talent Name", "Type", "Trx ID", "Amount", "Status"];
    const csvRows = [
      headers.join(","),
      ...rows.map((row: any) => {
        const values = [
          `"${dayjs(row?.created_at).format("MMM DD, YYYY hh:mm A")}"`,
          `"#${row?.user?.id} ${row?.user?.full_name || ""}"`,
          `"${(row?.type || "").toUpperCase()}"`,
          `"${row?.stripe_transfer_id || ""}"`,
          `"${formatMoney(parseInt(row?.total || "0"))}"`,
          `"${(row?.status || "").toUpperCase()}"`,
        ];
        return values.join(",");
      }),
    ];

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `Withdrawals_${dayjs().format("YYYY-MM-DD")}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [rows]);

  const columns: GridColDef[] = React.useMemo(
    () => [
      {
        field: "created_at",
        headerName: "Time",
        sortingOrder: ["desc", "asc", null],
        sortable: true,
        filterable: false,
        minWidth: isMobile ? 120 : 200,
        valueGetter: (params: GridValueGetterParams<undefined, Withdrawal>) =>
          `${dayjs(params.row?.created_at).format("MMM DD, YYYY hh:mm A")}`,
      },
      {
        field: "user.first_name",
        headerName: "Talent Name",
        minWidth: isMobile ? 150 : 200,
        flex: 1,
        sortable: true,
        filterable: true,
        valueGetter: (params: GridValueGetterParams<undefined, Withdrawal>) =>
          `#${params.row?.user?.id} ${params.row?.user?.full_name}`,
        renderCell: (params: GridRenderCellParams<undefined, Withdrawal>) => (
          <Link
            component="button"
            variant="body2"
            onClick={() => onViewTalent(params.row.user)}
            sx={{
              color: "#F4A259",
              textDecoration: "none",
              fontWeight: 500,
              fontSize: isMobile ? "0.75rem" : "0.875rem",
              "&:hover": {
                textDecoration: "underline",
                color: "#E8944A",
              },
            }}
          >
            #{params.row?.user?.id} {params.row?.user?.full_name}
          </Link>
        ),
        disableExport: true,
      },
      {
        field: "type",
        headerName: "Type",
        headerAlign: "center",
        sortable: true,
        filterable: true,
        minWidth: isMobile ? 80 : 100,
        align: "center",
        valueGetter: (params: GridValueGetterParams<undefined, Withdrawal>) =>
          params.row?.type?.toUpperCase() || "",
      },
      {
        field: "stripe_transfer_id",
        headerName: "Trx ID",
        headerAlign: "center",
        sortable: false,
        filterable: false,
        minWidth: isMobile ? 100 : 260,
        align: "center",
      },
      {
        field: "total",
        headerName: "Amount",
        headerAlign: "center",
        minWidth: isMobile ? 80 : 120,
        align: "right",
        type: "number",
        sortable: false,
        filterable: false,
        valueGetter: (params: GridValueGetterParams<undefined, Withdrawal>) =>
          `${formatMoney(parseInt(params.row?.total || "0"))}`,
      },
      {
        field: "status",
        headerName: "Status",
        headerAlign: "center",
        minWidth: isMobile ? 80 : 120,
        align: "center",
        sortable: true,
        filterable: true,
        renderCell: (params: GridRenderCellParams<undefined, Withdrawal>) => (
          <Chip
            label={params.row.status.toUpperCase()}
            size="small"
            color={params.row.status == "completed" ? "success" : undefined}
            sx={{ fontSize: isMobile ? 10 : 12 }}
          />
        ),
      },
    ],
    [onViewTalent, isMobile]
  );

  const getRowClassName = (params: GridRowClassNameParams) => {
    return !!params.row.deleted_at ? "deleted" : "";
  };

  return (
    <Box>
      <CardBase
        title="Withdrawals"
        useGlassCard={false}
        headerActions={
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems="stretch"
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            <StyledTextField
              size="small"
              label="Enter to search.."
              value={searchQuery || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
              sx={{
                minWidth: { xs: "100%", sm: 250 },
                width: { xs: "100%", sm: "auto" },
              }}
            />
            {!!searchQuery && (
              <Button
                variant="text"
                onClick={() => setSearchQuery("")}
                fullWidth={isMobile}
                sx={{
                  color: "#F4A259",
                  "&:hover": {
                    backgroundColor: "rgba(244, 162, 89, 0.08)",
                  },
                }}
              >
                Reset
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              disabled={!rows || rows.length === 0}
              size="small"
              fullWidth={isMobile}
              sx={{
                borderColor: "#F4A259",
                color: "#F4A259",
                "&:hover": {
                  backgroundColor: "rgba(244, 162, 89, 0.08)",
                  borderColor: "#F4A259",
                },
                "&:disabled": {
                  borderColor: "rgba(244, 162, 89, 0.5)",
                  color: "rgba(244, 162, 89, 0.5)",
                },
              }}
            >
              {isMobile ? "Export" : "Export CSV"}
            </Button>
          </Stack>
        }
        sx={{ height: "85vh", p: 0 }}
      >
        <GlassDataGrid
          rows={rows || []}
          columns={columns}
          paginationMode="server"
          rowCount={rowCount}
          page={page}
          onPageChange={(newPage: number) => setPage(newPage)}
          pageSize={pageSize}
          rowsPerPageOptions={[25, 50, 100]}
          onPageSizeChange={(newSize: number) => setPageSize(newSize)}
          sortModel={sortModel}
          sortingMode="server"
          onSortModelChange={(newSortModel: GridSortModel) => setSortModel(newSortModel)}
          loading={isLoading || isFetching}
          components={{
            LoadingOverlay: OrangeLinearProgress,
            NoResultsOverlay: NoResultsOverlay,
          }}
          disableSelectionOnClick
          disableColumnFilter
          height={isMobile ? "calc(85vh - 120px)" : "calc(85vh - 80px)"}
          sx={{
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "transparent",
              borderTop: "none",
              paddingLeft: isMobile ? theme.spacing(0.5) : theme.spacing(2),
              paddingRight: isMobile ? theme.spacing(0.5) : theme.spacing(2),
              "& .MuiDataGrid-columnHeaderTitle": {
                color: "#F4A259",
                fontWeight: 600,
                fontSize: isMobile ? "0.75rem" : "0.875rem",
              },
              "& .MuiDataGrid-columnHeader": {
                color: "#F4A259",
                "& .MuiDataGrid-iconButtonContainer": {
                  color: "#F4A259",
                },
                "& .MuiDataGrid-sortIcon": {
                  color: "#F4A259",
                },
              },
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "rgba(244, 162, 89, 0.05)",
            },
            "& .MuiDataGrid-cell": {
              paddingLeft: isMobile ? theme.spacing(0.5) : theme.spacing(2),
              paddingRight: isMobile ? theme.spacing(0.5) : theme.spacing(2),
              py: isMobile ? 0.5 : 1,
              fontSize: isMobile ? "0.75rem" : "0.875rem",
            },
            "& .deleted": {
              fontStyle: "italic",
              color: "#aaa",
            },
          }}
          getRowClassName={getRowClassName}
        />
        <TalentDrawer
          data={currentTalent!}
          open={openTalent}
          setOpen={setOpenTalent}
          forceRefresh={true}
        />
      </CardBase>
    </Box>
  );
};
export default WithdrawalsPage;
